/**
 * Displays the public trial-expired page with plan and administrator contact links.
 */
import { Box, Button, Stack, Typography } from '@mui/material';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';

import trialExpiredImage from 'assets/images/maintenance/trial-expired.png';

const styles = {
    root: {
        minHeight: '100svh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fbfdfd',
        color: '#141a38'
    },
    header: { px: { xs: 2.5, sm: 3.5 }, pt: 2, pb: 1 },
    content: {
        flex: 1,
        width: '100%',
        maxWidth: 900,
        mx: 'auto',
        px: { xs: 3, sm: 4 },
        pt: 1,
        pb: { xs: 4, sm: 6 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center'
    },
    illustration: {
        display: 'block',
        width: '100%',
        maxWidth: 650,
        maxHeight: { xs: 240, sm: 320 },
        objectFit: 'contain',
        mb: { xs: 3, sm: 4 }
    },
    title: { fontWeight: 700, lineHeight: 1.2, color: '#101535' },
    lead: { mt: 2.5, lineHeight: 1.5 },
    description: { mt: 2, maxWidth: 740, lineHeight: 1.55, color: '#6b7286' },
    actions: { mt: { xs: 3, sm: 4 }, width: '100%', maxWidth: 460 },
    button: {
        minHeight: 56,
        px: 2,
        borderRadius: 2,
        fontSize: { xs: '1rem', sm: '1.125rem' },
        fontWeight: 700,
        textTransform: 'none',
        '&.MuiButton-contained': { bgcolor: '#159f8c', color: '#fff', '&:hover': { bgcolor: '#108573' } },
        '&.MuiButton-outlined': { color: '#118a79', borderColor: '#19b89f', '&:hover': { bgcolor: '#edf9f6', borderColor: '#118a79' } }
    }
};

/**
 * Explain trial expiry without requiring a session, using public organisation contact details.
 * @returns {JSX.Element} Trial expiry notice and recovery actions.
 */
export default function TrialExpired() {
    return (
        <Box sx={styles.root}>
            <Box component="main" aria-labelledby="trial-expired-title" sx={styles.content}>
                <Box component="img" src={trialExpiredImage} alt="" sx={styles.illustration} />
                <Typography id="trial-expired-title" variant="h1" className="main-font-b" sx={styles.title}>
                    Your trial has expired
                </Typography>
                <Typography className="main-font" variant="h4" sx={styles.lead}>
                    This account is no longer active as the trial period has ended.
                </Typography>
                <Typography className="main-font" variant="body1" sx={styles.description}>
                    To continue using TLRG RPL Robot and access assessments, candidates, reports and all features, please upgrade to a paid
                    plan or contact your administrator.
                </Typography>
                <Stack spacing={2} sx={styles.actions}>
                    <Button
                        variant="contained"
                        fullWidth
                        disableElevation
                        href={process.env.REACT_APP_PLANS_URL || 'https://tlrg.com.au/pages/rpl-robot'}
                        target="_blank"
                        rel="noopener noreferrer"
                        endIcon={<OpenInNewRounded />}
                        className="main-font-b"
                        sx={styles.button}
                    >
                        View Plans
                    </Button>
                </Stack>
            </Box>
        </Box>
    );
}
